





















































































import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { ButtonType, IconSize } from '@getaccept/lib-ui';
import type { ComputedRef, PropType } from '@vue/composition-api';
import { computed, defineComponent, ref, toRefs, watch, nextTick } from '@vue/composition-api';
import debounce from 'debounce';
import type { MentionParticipant } from '../../mentions/types/mention-participant';
import { MentionsHelper } from '../../mentions/helpers/mentions.helper';
import type { Recipient } from '../../../types/Recipient';
import type { User } from '../../../users/types/user';
import LinkButton from '../../components/LinkButton.vue';
import { useReadEvents } from '../../composables/read-events.composable';
import CommentInput from '../../components/CommentInput.vue';
import CommentTargetText from '../../components/CommentTargetText.vue';
import MessageContainer from '../../components/MessageContainer.vue';
import { CommentsHelper } from '../../helpers/comments.helper';
import type { Comment } from '../../types/comment';
import { CommentStatus } from '../../types/comment-status';
import type { CurrentUser } from '../../types/current-user';
import type { UpdateCommentDto } from '../../types/update-comment-dto';
import { useCommentMetadata } from '../../composables/comment-metadata.composable';
import CarouselControls from './CarouselControls.vue';
import PulsingLine from './PulsingLine.vue';

export default defineComponent({
  components: {
    LinkButton,
    CarouselControls,
    CommentTargetText,
    MessageContainer,
    CommentInput,
    PulsingLine,
  },
  props: {
    comments: {
      type: Array as PropType<Comment[]>,
      default: () => [],
    },
    currentUser: {
      type: Object as PropType<CurrentUser>,
      required: true,
    },
    selectedId: {
      type: String,
      required: true,
    },
    recipients: {
      type: Array as PropType<Recipient[]>,
      required: true,
    },
    sender: {
      type: Object as PropType<User>,
      required: true,
    },
    readOnly: {
      type: Boolean,
    },
    mentionsEnabled: {
      type: Boolean,
    },
  },
  setup(props, { emit }) {
    const { comments, currentUser, selectedId, recipients, sender, readOnly } = toRefs(props);

    const currentCommentIndex = ref(comments.value.findIndex(({ id }) => id === selectedId.value));
    const currentComment = computed(() => comments.value[currentCommentIndex.value]);

    const replyEngaged = ref(false);

    const {
      isDraft,
      isResolved,
      isGhost,
      isResolvable,
      richTextValue,
      visibleReplies,
    } = useCommentMetadata(currentComment, currentUser, readOnly, replyEngaged);

    const move = (offset: number) => {
      const newIndex = currentCommentIndex.value + offset;
      currentCommentIndex.value =
        newIndex < 0 ? comments.value.length - 1 : newIndex >= comments.value.length ? 0 : newIndex;

      emit('select', currentComment.value.id);
    };

    watch(
      () => selectedId.value,
      id => {
        const index = comments.value.findIndex(comment => comment.id === id);
        currentCommentIndex.value = index === -1 ? 0 : index;
      },
      { immediate: true }
    );

    const resolve = () => {
      const payload: Partial<UpdateCommentDto> = {
        commentId: currentComment.value.id,
        status: CommentStatus.Resolved,
      };
      emit('update', payload);
    };

    const emitText = ({ richText, text }: { richText: string; text: string }) => {
      const payload: Partial<UpdateCommentDto> = {
        richText,
        text,
        commentId: currentComment.value.id,
      };
      emit('update', payload);
    };

    const emitTextDebounce = debounce(emitText, 300);

    const handleSubmit = ({ richText, text }: { richText: string; text: string }) => {
      if (isDraft.value) {
        emitTextDebounce.clear();
        const payload: Partial<UpdateCommentDto> = {
          commentId: currentComment.value.id,
          richText,
          text,
          status: CommentStatus.Active,
        };
        emit('update', payload);
      } else {
        emit('create-reply', { commentId: currentComment.value.id, richText, text });
      }
    };

    const cancelComment = async () => {
      if (isDraft.value) {
        emit('delete', selectedId.value);
        await nextTick();
        move(-1);
        return;
      }
      replyEngaged.value = false;
    };

    const { mappedLatestReadEvents } = useReadEvents(
      currentComment,
      recipients.value,
      sender.value,
      currentUser
    );

    const mentionParticipants: ComputedRef<MentionParticipant[]> = computed(() =>
      MentionsHelper.getMentionParticipants(recipients.value, sender.value)
    );

    const filteredMentionParticipants: ComputedRef<MentionParticipant[]> = computed(() =>
      MentionsHelper.filterIneligibleParticipants(currentUser.value, mentionParticipants.value)
    );

    return {
      IconSize,
      ButtonType,

      currentComment,
      currentCommentIndex,
      visibleReplies,
      mappedLatestReadEvents,

      isDraft,
      isResolved,
      isGhost,
      isResolvable,
      replyEngaged,
      richTextValue,

      t,
      move,
      isRead: CommentsHelper.isRead,
      isCommentRead: CommentsHelper.isCommentRead,
      getUserMetaData: CommentsHelper.getUserMetaData,
      resolve,
      handleSubmit,
      cancelComment,
      emitTextDebounce,
      mentionParticipants,
      filteredMentionParticipants,
    };
  },
});
